import moment from 'moment'
import {
  CATCH_BOARD_DEFAULT_PRIORITY,
  CATCH_BOARD_DEFAULT_READINESS,
  CATCH_BOARD_DEFAULT_STAGE,
  CATCH_BOARD_MAX_BUYERS_PER_ROLE,
  CATCH_BOARD_MAX_LINKED_OPPORTUNITIES,
  CATCH_BOARD_MAX_LINKED_VENUES,
  CATCH_BOARD_READINESS_NOTE_MAX_LENGTH,
  CATCH_BOARD_STAGE,
  CATCH_BOARD_STAGES,
  isCatchBoardReadiness,
  lostReasonRequiresNote,
  readinessRequiresNote,
} from 'src/screens/catch-board/catchBoardConstants'
import {
  normalizeCatchBoardBuyers,
  serializeCatchBoardBuyers,
} from 'src/utils/catchBoardBuyerHelpers'

/**
 * Convert GBP pounds input to integer pence for the API.
 * @param {number|string|null|undefined} pounds
 * @returns {number|null}
 */
export function poundsInputToPence(pounds) {
  if (pounds == null || pounds === '') {
    return null
  }

  const value = Number(pounds)
  if (!Number.isFinite(value)) {
    return null
  }

  return Math.round(value * 100)
}

/**
 * Convert integer pence to pounds for InputNumber display.
 * @param {number|null|undefined} pence
 * @returns {number|null}
 */
export function penceToPoundsInput(pence) {
  if (pence == null || pence === '') {
    return null
  }

  const value = Number(pence)
  if (!Number.isFinite(value)) {
    return null
  }

  return value / 100
}

/**
 * @param {string|Date|null|undefined} value
 * @returns {moment.Moment|null}
 */
export function toCatchBoardDatePickerValue(value) {
  if (!value) {
    return null
  }

  const parsed = moment(value)
  return parsed.isValid() ? parsed : null
}

/**
 * @param {moment.Moment|null|undefined} value
 * @returns {string|null} ISO string or null
 */
export function fromCatchBoardDatePickerValue(value) {
  if (!value || !value.isValid?.()) {
    return null
  }

  return value.toISOString()
}

/**
 * Normalize linked venue chips for the deal modal.
 * @param {Array<{ venueId?: string, name?: string }|string>} venues
 * @returns {Array<{ venueId: string, name: string }>}
 */
export function normalizeCatchBoardLinkedVenues(venues = []) {
  const seen = new Set()
  const result = []

  venues.forEach(entry => {
    const venueId =
      typeof entry === 'string' || typeof entry === 'number'
        ? String(entry)
        : entry?.venueId
          ? String(entry.venueId)
          : ''
    if (!venueId || seen.has(venueId)) {
      return
    }
    seen.add(venueId)
    const name =
      typeof entry === 'object' && entry?.name
        ? String(entry.name).trim()
        : ''
    result.push({
      venueId,
      name: name || `Venue · ${venueId.slice(-6)}`,
    })
  })

  return result
}

/**
 * Build linked venue chips from a loaded deal (+ optional name hints from the board).
 * @param {object} deal
 * @param {{ primaryVenueNameHint?: string }} [options]
 */
export function buildCatchBoardLinkedVenuesFromDeal(
  deal = {},
  { primaryVenueNameHint = '' } = {},
) {
  const primaryVenueId = deal.primaryVenueId
    ? String(deal.primaryVenueId)
    : null
  const primaryName = deal.primaryVenueName || primaryVenueNameHint || ''

  const ids = Array.isArray(deal.linkedVenueIds)
    ? deal.linkedVenueIds
    : primaryVenueId
      ? [primaryVenueId]
      : []

  return normalizeCatchBoardLinkedVenues(
    ids.map(venueId => ({
      venueId,
      name:
        String(venueId) === primaryVenueId && primaryName ? primaryName : '',
    })),
  )
}

/**
 * Link a venue; first link becomes primary.
 * @param {{ linkedVenues?: object[], primaryVenueId?: string|null }} form
 * @param {{ venueId: string, name?: string }} venue
 */
export function linkCatchBoardVenue(form = {}, venue) {
  const venueId = venue?.venueId ? String(venue.venueId) : ''
  if (!venueId) {
    return form
  }

  const existing = normalizeCatchBoardLinkedVenues(form.linkedVenues)
  if (existing.some(row => row.venueId === venueId)) {
    return form
  }

  if (existing.length >= CATCH_BOARD_MAX_LINKED_VENUES) {
    return form
  }

  const linkedVenues = [
    ...existing,
    {
      venueId,
      name: String(venue.name || '').trim() || `Venue · ${venueId.slice(-6)}`,
    },
  ]

  return {
    ...form,
    linkedVenues,
    primaryVenueId: form.primaryVenueId || venueId,
  }
}

/**
 * Unlink a venue; reassign primary when needed.
 * @param {{ linkedVenues?: object[], primaryVenueId?: string|null }} form
 * @param {string} venueId
 */
export function unlinkCatchBoardVenue(form = {}, venueId) {
  const target = venueId ? String(venueId) : ''
  const linkedVenues = normalizeCatchBoardLinkedVenues(form.linkedVenues).filter(
    row => row.venueId !== target,
  )

  let primaryVenueId = form.primaryVenueId
    ? String(form.primaryVenueId)
    : null
  if (primaryVenueId === target) {
    primaryVenueId = linkedVenues[0]?.venueId || null
  }

  return {
    ...form,
    linkedVenues,
    primaryVenueId,
  }
}

/**
 * @param {{ linkedVenues?: object[], primaryVenueId?: string|null }} form
 * @param {string} venueId
 */
export function setCatchBoardPrimaryVenue(form = {}, venueId) {
  const target = venueId ? String(venueId) : ''
  const linkedVenues = normalizeCatchBoardLinkedVenues(form.linkedVenues)
  if (!target || !linkedVenues.some(row => row.venueId === target)) {
    return form
  }

  return {
    ...form,
    linkedVenues,
    primaryVenueId: target,
  }
}

/**
 * Empty create form. Owner defaults to the current account when provided.
 * @param {{ accountId?: string|null, stage?: string }} [options]
 */
export function buildEmptyCatchBoardDealForm({
  accountId = null,
  stage = CATCH_BOARD_DEFAULT_STAGE,
} = {}) {
  return {
    name: '',
    companyName: '',
    description: '',
    stage: stage || CATCH_BOARD_DEFAULT_STAGE,
    dealValuePounds: null,
    potentialDealValuePounds: null,
    ownerAccountId: accountId || null,
    priority: CATCH_BOARD_DEFAULT_PRIORITY,
    leadSource: null,
    leadSourceDetail: '',
    expectedCloseDate: null,
    nextFollowUpAt: null,
    linkedVenues: [],
    primaryVenueId: null,
    linkedOpportunityIds: [],
    economicBuyers: [],
    userBuyers: [],
    wonPlan: null,
    wonMrrPounds: null,
    wonGoLiveDate: null,
    lostReason: null,
    lostReasonNote: '',
    readiness: CATCH_BOARD_DEFAULT_READINESS,
    readinessNote: '',
  }
}

/**
 * Map API / board deal into modal form state.
 * @param {object} deal
 * @param {{ primaryVenueNameHint?: string }} [options]
 */
export function catchBoardDealToFormValues(
  deal = {},
  { primaryVenueNameHint = '' } = {},
) {
  const linkedVenues = buildCatchBoardLinkedVenuesFromDeal(deal, {
    primaryVenueNameHint,
  })
  let primaryVenueId = deal.primaryVenueId
    ? String(deal.primaryVenueId)
    : null
  if (
    primaryVenueId &&
    !linkedVenues.some(row => row.venueId === primaryVenueId)
  ) {
    primaryVenueId = null
  }
  if (!primaryVenueId && linkedVenues.length === 1) {
    primaryVenueId = linkedVenues[0].venueId
  }

  return {
    name: deal.name || '',
    companyName: deal.companyName || '',
    description: deal.description || '',
    stage: deal.stage || CATCH_BOARD_DEFAULT_STAGE,
    dealValuePounds: penceToPoundsInput(deal.dealValuePence),
    potentialDealValuePounds: penceToPoundsInput(deal.potentialDealValuePence),
    ownerAccountId: deal.ownerAccountId || null,
    priority: deal.priority || CATCH_BOARD_DEFAULT_PRIORITY,
    leadSource: deal.leadSource ?? null,
    leadSourceDetail: deal.leadSourceDetail || '',
    expectedCloseDate: toCatchBoardDatePickerValue(deal.expectedCloseDate),
    nextFollowUpAt: toCatchBoardDatePickerValue(deal.nextFollowUpAt),
    linkedVenues,
    primaryVenueId,
    linkedOpportunityIds: Array.isArray(deal.linkedOpportunityIds)
      ? deal.linkedOpportunityIds.map(String).filter(Boolean)
      : [],
    economicBuyers: normalizeCatchBoardBuyers(deal.economicBuyers),
    userBuyers: normalizeCatchBoardBuyers(deal.userBuyers),
    wonPlan: deal.wonPlan ?? null,
    wonMrrPounds: penceToPoundsInput(deal.wonMrrPence),
    wonGoLiveDate: toCatchBoardDatePickerValue(deal.wonGoLiveDate),
    lostReason: deal.lostReason ?? null,
    lostReasonNote: deal.lostReasonNote || '',
    readiness: isCatchBoardReadiness(deal.readiness)
      ? deal.readiness
      : CATCH_BOARD_DEFAULT_READINESS,
    readinessNote: deal.readinessNote || '',
  }
}

/**
 * Stages selectable in the deal modal (includes Landed / Got Away).
 */
export function getCatchBoardCoreStageOptions() {
  return [...CATCH_BOARD_STAGES]
}

/**
 * Build create/update API body from form state.
 * @param {object} form
 * @param {{ includeStage?: boolean }} [options]
 */
export function buildCatchBoardDealWriteBody(
  form = {},
  { includeStage = true } = {},
) {
  const name = String(form.name || '').trim()
  const linkedVenues = normalizeCatchBoardLinkedVenues(form.linkedVenues)
  let primaryVenueId = form.primaryVenueId
    ? String(form.primaryVenueId)
    : null

  if (
    primaryVenueId &&
    !linkedVenues.some(row => row.venueId === primaryVenueId)
  ) {
    primaryVenueId = null
  }
  if (!primaryVenueId && linkedVenues.length > 0) {
    primaryVenueId = linkedVenues[0].venueId
  }

  const body = {
    name,
    companyName: String(form.companyName || '').trim(),
    description: String(form.description || '').trim(),
    dealValuePence: poundsInputToPence(form.dealValuePounds),
    potentialDealValuePence: poundsInputToPence(form.potentialDealValuePounds),
    ownerAccountId: form.ownerAccountId || null,
    priority: form.priority || CATCH_BOARD_DEFAULT_PRIORITY,
    leadSource: form.leadSource || null,
    leadSourceDetail: String(form.leadSourceDetail || '').trim(),
    expectedCloseDate: fromCatchBoardDatePickerValue(form.expectedCloseDate),
    nextFollowUpAt: fromCatchBoardDatePickerValue(form.nextFollowUpAt),
    linkedVenueIds: linkedVenues.map(row => row.venueId),
    primaryVenueId,
    linkedOpportunityIds: (Array.isArray(form.linkedOpportunityIds)
      ? form.linkedOpportunityIds
      : []
    )
      .map(id => String(id || '').trim())
      .filter(Boolean)
      .slice(0, CATCH_BOARD_MAX_LINKED_OPPORTUNITIES),
    economicBuyers: serializeCatchBoardBuyers(form.economicBuyers),
    userBuyers: serializeCatchBoardBuyers(form.userBuyers),
    readiness: isCatchBoardReadiness(form.readiness) ? form.readiness : null,
    readinessNote: '',
  }

  if (readinessRequiresNote(body.readiness)) {
    body.readinessNote = String(form.readinessNote || '').trim()
  }

  if (includeStage && form.stage) {
    body.stage = form.stage
  }

  const stage = form.stage || body.stage
  const includeWonFields =
    stage === CATCH_BOARD_STAGE.LANDED ||
    form.wonPlan != null ||
    form.wonMrrPounds != null ||
    form.wonGoLiveDate != null
  const includeLostFields =
    stage === CATCH_BOARD_STAGE.GOT_AWAY ||
    form.lostReason != null ||
    Boolean(String(form.lostReasonNote || '').trim())

  if (includeWonFields) {
    body.wonPlan = form.wonPlan || null
    body.wonMrrPence = poundsInputToPence(form.wonMrrPounds)
    body.wonGoLiveDate = fromCatchBoardDatePickerValue(form.wonGoLiveDate)
  }

  if (includeLostFields) {
    body.lostReason = form.lostReason || null
    body.lostReasonNote = String(form.lostReasonNote || '').trim()
  }

  return body
}

/**
 * Field-level Landed / Got Away errors (all missing fields at once).
 * @param {object} [form]
 * @returns {Record<string, string>}
 */
export function getCatchBoardClosedStageFieldErrors(form = {}) {
  const errors = {}

  if (form.stage === CATCH_BOARD_STAGE.LANDED) {
    if (!form.wonPlan) {
      errors.wonPlan = 'Plan sold is required for Landed'
    }
    if (
      form.wonMrrPounds == null ||
      form.wonMrrPounds === '' ||
      !Number.isFinite(Number(form.wonMrrPounds))
    ) {
      errors.wonMrrPounds = 'Won MRR is required for Landed'
    }
    if (!form.wonGoLiveDate || !form.wonGoLiveDate.isValid?.()) {
      errors.wonGoLiveDate = 'Go-live date is required for Landed'
    }
  }

  if (form.stage === CATCH_BOARD_STAGE.GOT_AWAY) {
    if (!form.lostReason) {
      errors.lostReason = 'Lost reason is required for Got Away'
    }
    if (
      lostReasonRequiresNote(form.lostReason) &&
      !String(form.lostReasonNote || '').trim()
    ) {
      errors.lostReasonNote = 'Add a note when lost reason is Other'
    }
  }

  return errors
}

/**
 * Full create/edit modal validation.
 * Closed-stage failures include fieldErrors + scrollTo for inline UX.
 *
 * @param {object} [form]
 * @returns {{
 *   message: string,
 *   fieldErrors?: Record<string, string>,
 *   scrollTo?: 'closed-fields'|null,
 * }|null}
 */
export function getCatchBoardDealFormValidation(form = {}) {
  const name = String(form.name || '').trim()
  if (!name) {
    return { message: 'Deal name is required', fieldErrors: {}, scrollTo: null }
  }

  const linkedVenues = normalizeCatchBoardLinkedVenues(form.linkedVenues)
  if (linkedVenues.length > 1) {
    const primaryVenueId = form.primaryVenueId
      ? String(form.primaryVenueId)
      : ''
    if (
      !primaryVenueId ||
      !linkedVenues.some(row => row.venueId === primaryVenueId)
    ) {
      return {
        message: 'Pick a primary venue',
        fieldErrors: {},
        scrollTo: null,
      }
    }
  }

  if (linkedVenues.length > CATCH_BOARD_MAX_LINKED_VENUES) {
    return {
      message: `You can link at most ${CATCH_BOARD_MAX_LINKED_VENUES} venues`,
      fieldErrors: {},
      scrollTo: null,
    }
  }

  const economicBuyers = normalizeCatchBoardBuyers(form.economicBuyers)
  const userBuyers = normalizeCatchBoardBuyers(form.userBuyers)
  if (economicBuyers.length > CATCH_BOARD_MAX_BUYERS_PER_ROLE) {
    return {
      message: `You can add at most ${CATCH_BOARD_MAX_BUYERS_PER_ROLE} economic buyers`,
      fieldErrors: {},
      scrollTo: null,
    }
  }
  if (userBuyers.length > CATCH_BOARD_MAX_BUYERS_PER_ROLE) {
    return {
      message: `You can add at most ${CATCH_BOARD_MAX_BUYERS_PER_ROLE} user buyers`,
      fieldErrors: {},
      scrollTo: null,
    }
  }

  const closedFieldErrors = getCatchBoardClosedStageFieldErrors(form)
  if (Object.keys(closedFieldErrors).length) {
    return {
      message:
        form.stage === CATCH_BOARD_STAGE.LANDED
          ? 'Complete the Landed details'
          : 'Complete the Got Away details',
      fieldErrors: closedFieldErrors,
      scrollTo: 'closed-fields',
    }
  }

  if (
    readinessRequiresNote(form.readiness) &&
    !String(form.readinessNote || '').trim()
  ) {
    return {
      message: 'Add a note when customer success or product is needed',
      fieldErrors: {},
      scrollTo: null,
    }
  }

  if (
    String(form.readinessNote || '').length >
    CATCH_BOARD_READINESS_NOTE_MAX_LENGTH
  ) {
    return {
      message: `Readiness note must be at most ${CATCH_BOARD_READINESS_NOTE_MAX_LENGTH} characters`,
      fieldErrors: {},
      scrollTo: null,
    }
  }

  return null
}

/**
 * Client-side required-field check for deal modal save.
 * @param {object} form
 * @returns {string|null} error message
 */
export function validateCatchBoardCoreDealForm(form = {}) {
  const result = getCatchBoardDealFormValidation(form)
  if (!result) {
    return null
  }
  if (result.scrollTo === 'closed-fields' && result.fieldErrors) {
    const firstFieldMessage = Object.values(result.fieldErrors)[0]
    return firstFieldMessage || result.message
  }
  return result.message
}
